// React imports
import React, { useState, useEffect, useRef } from 'react';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import Divider from '@mui/material/Divider';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/pro-light-svg-icons';

// Generic imports
import axios from 'axios';

// Trusted Types safe: assigning a raw string to `.innerHTML` is a Trusted Types
// sink and is blocked when the document enforces require-trusted-types-for 'script'.
// DOMParser takes a string and returns a Document without hitting a sink, so we
// build nodes that way and move them into the live DOM (insertion auto-adopts them).
function parseHTML(html) {
    return new DOMParser().parseFromString(html || '', 'text/html');
}

function setEditorHTML(el, html) {
    if (!el) return;
    const doc = parseHTML(html);
    el.replaceChildren(...Array.from(doc.body.childNodes));
}

function insertNodeAtCaret(editor, node) {
    if (!editor) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editor.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    } else {
        editor.appendChild(node);
    }
}

// A faithful "paste your signature" editor: a contenteditable area that keeps the
// rich HTML pasted from Outlook/Gmail verbatim (TipTap would strip tables/styles).
// On save, any inline base64 images are uploaded to S3 so they render in sent mail.
function SignatureEditor(props) {
    const { me, accesstoken, organizationId, onSignatureSaved } = props;
    const [signature, setSignature] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [imageWarning, setImageWarning] = useState(0);
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadSignature();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me?.email]);

    async function loadSignature() {
        if (!me?.email) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`/api/users/signature?accesstoken=${accesstoken}&organizationId=${organizationId}&email=${encodeURIComponent(me.email)}`);
            const html = data?.signature || '';
            setSignature(html);
            setEditorHTML(editorRef.current, html);
        } catch (e) {
            console.error('Error loading signature:', e);
        } finally {
            setLoading(false);
        }
    }

    async function uploadImage(dataUrl, contentType) {
        const { data } = await axios.post(
            `/api/users/signature-image?accesstoken=${accesstoken}&organizationId=${organizationId}`,
            { image: dataUrl, contentType }
        );
        return data.url;
    }

    function blobToDataUrl(blob) {
        return new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(blob);
        });
    }

    // Re-host pasted images onto our own storage so they render in sent mail.
    // - data:  (base64)  → upload directly.
    // - blob:  (browser session URL) → readable same-origin, so fetch → upload.
    // - cid: / webmail attachment links (e.g. mail.google.com, Outlook) → these point at the
    //   sender's private mailbox and can't be fetched (auth/CORS), so they can't be auto-hosted.
    //   They're collected and returned so the user can be told to re-add them via Insert image.
    async function hostInlineImages(html) {
        const doc = parseHTML(html);
        const imgs = Array.from(doc.querySelectorAll('img'));
        const broken = [];
        for (const img of imgs) {
            const src = img.getAttribute('src') || '';
            if (src.startsWith('data:')) {
                try { img.setAttribute('src', await uploadImage(src)); } catch { broken.push(src); }
            } else if (src.startsWith('blob:')) {
                try {
                    const blob = await (await fetch(src)).blob();
                    img.setAttribute('src', await uploadImage(await blobToDataUrl(blob), blob.type));
                } catch { broken.push(src); }
            } else if (src.startsWith('cid:') || /mail\.google\.com|googleusercontent\.com\/[^"']*#|outlook\.(office|office365|live)\.com|attachments?\.office\.net|\/OWA\/|\bpermmsgid=/i.test(src)) {
                // Private webmail image — will break for recipients; flag it (leave src so the
                // user still sees where it was, and can replace it).
                broken.push(src);
            }
            // Other public http(s) URLs are left as-is (they may be legitimately hosted).
        }
        // Reading innerHTML is a getter, not a Trusted Types sink — safe to return.
        return { html: doc.body.innerHTML, broken };
    }

    async function handleSave() {
        setSaving(true);
        setError(null);
        setSuccess(false);
        setImageWarning(0);
        try {
            const raw = editorRef.current ? editorRef.current.innerHTML : signature;
            const { html: hosted, broken } = await hostInlineImages(raw);
            await axios.post(
                `/api/users/signature?accesstoken=${accesstoken}&organizationId=${organizationId}&email=${encodeURIComponent(me.email)}`,
                { signature: hosted }
            );
            setSignature(hosted);
            setEditorHTML(editorRef.current, hosted);
            if (onSignatureSaved) onSignatureSaved(hosted);
            // Some images point at the sender's private Gmail/Outlook mailbox — they show here
            // but break for recipients, and we can't auto-host them. Tell the user to re-add.
            if (broken.length > 0) {
                setImageWarning(broken.length);
            } else {
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            }
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Failed to save signature');
        } finally {
            setSaving(false);
        }
    }

    function handleImageUpload(event) {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const url = await uploadImage(e.target.result, file.type);
                if (editorRef.current) {
                    editorRef.current.focus();
                    const img = document.createElement('img');
                    img.src = url;
                    img.alt = '';
                    img.style.maxWidth = '300px';
                    insertNodeAtCaret(editorRef.current, img);
                    setSignature(editorRef.current.innerHTML);
                }
            } catch (err) {
                setError('Image upload failed');
            }
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    }

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, mb: 0.5 }}>
                    My Email Signature
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'Poppins, sans-serif' }}>
                    Paste your signature from Outlook/Gmail below (formatting and images are kept). It's added automatically to emails you send from Monada.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>Signature saved.</Alert>
            )}
            {imageWarning > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setImageWarning(0)}>
                    Saved, but {imageWarning} image{imageWarning > 1 ? 's are' : ' is'} linked to your Gmail/Outlook mailbox and <b>won't display for recipients</b> (they only load for you). Click each broken image and use <b>Insert image</b> to upload {imageWarning > 1 ? 'them' : 'it'}, then save again.
                </Alert>
            )}

            <Box sx={{ mb: 1 }}>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon={faImage} />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ textTransform: 'none', fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                >
                    Insert image
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box
                ref={editorRef}
                contentEditable={!loading}
                suppressContentEditableWarning
                onInput={() => setSignature(editorRef.current?.innerHTML || '')}
                sx={{
                    minHeight: '180px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 2,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    overflowX: 'auto',
                    backgroundColor: '#ffffff',
                    '&:focus': { borderColor: '#4F46E5' },
                    '& img': { maxWidth: '100%' },
                }}
            />
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                Tip: in Outlook, select your whole signature, copy, then paste here.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <LoadingButton
                    variant="contained"
                    onClick={handleSave}
                    loading={saving}
                    disabled={loading}
                    sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', backgroundColor: '#4F46E5', '&:hover': { backgroundColor: '#4338CA' } }}
                >
                    Save Signature
                </LoadingButton>
            </Box>
        </Box>
    );
}

export default SignatureEditor;
